import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { ColumnDef } from '@tanstack/react-table';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import Dropdown from '../../components/Dropdown';
import { DropdownItem } from '../../components/Dropdown/config';
import SearchableClientDropdown from '../../components/SearchableClientDropdown';
import TableSkeleton from '../../components/TableSkeleton';
import TextInput from '../../components/TextInput';
import StatusPill, { PillTone } from '../../components/StatusPill';
import ListPage, { FilterTrigger, SearchInput, StatusSegments, useNarrowViewport } from '../../components/ListPage';
import { useServerFiltering } from '../../utils/useServerFiltering';
import { DATE_RANGE_PRESETS, getInvoiceDateRange } from '../InvoiceRegister/config';
import {
  FeeRegisterRow,
  FeeRegisterSummary,
  FeeOutcome,
  DECIDED_BUCKETS,
  DEFAULT_PAGE_SIZE,
  SEARCH_DEBOUNCE_DELAY,
  DEFAULT_TYPE_FILTER,
  DEFAULT_DATE_RANGE_PRESET,
  ALL_BUCKETS,
  FEE_BUCKET_PILLS,
  PROJECT_TYPE_OPTIONS,
  formatCurrency,
  formatDaysSince,
  formatDate,
} from './config';
import JobCardModal from '../JobCards/components/JobCardModal';
import { useRestoredJobCardModal } from '../JobCards/components/JobCardModal/restore';
import { JobCard } from '../JobCards/config';
import ChangeOutcomeModal from './components/ChangeOutcomeModal';
import './styles.scss';

// The pipeline buckets in the pill's vocabulary; Won/Lost share their colours
// with the row's outcome buttons so a row reads as one thought.
const feeBucketTone: Record<string, PillTone> = {
  'Awaiting Fee': 'neutral',
  Pending: 'warning',
  Won: 'success',
  Lost: 'danger',
};

const Fees: React.FC = () => {
  const session = useSession();
  const supabase = useSupabaseClient();

  const [searchInput, setSearchInput] = useState('');
  const [typeFilter, setTypeFilter] = useState<string[]>(DEFAULT_TYPE_FILTER);
  const [bucketFilter, setBucketFilter] = useState<string>(ALL_BUCKETS);
  const [clientFilter, setClientFilter] = useState<string>('');
  const [dateRangePreset, setDateRangePreset] = useState<string>(DEFAULT_DATE_RANGE_PRESET);
  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Row key awaiting a second click to confirm "Lost", and the row currently
  // mid-RPC (so its buttons can be disabled without freezing the whole table).
  const [confirmLostKey, setConfirmLostKey] = useState<string | null>(null);
  const [actioningKey, setActioningKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [changeRow, setChangeRow] = useState<FeeRegisterRow | null>(null);

  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [selectedStageId, setSelectedStageId] = useState<number | null>(null);
  const [selectedRow, setSelectedRow] = useState<FeeRegisterRow | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    data: rows,
    pagination,
    summary,
    loading,
    filtering,
    error,
    setFilters,
    refetch,
  } = useServerFiltering<FeeRegisterRow>({
    rpcFunction: 'get_filtered_project_fees',
    defaultFilters: {
      search_term: null,
      types_filter: DEFAULT_TYPE_FILTER,
      client_filter: null,
      status_filter: null,
      workflow_status_ids: null,
      bucket_filter: null,
      from_date: null,
      to_date: null,
      page_size: DEFAULT_PAGE_SIZE,
      page_offset: 0,
      sort_by: 'job_number',
      sort_order: 'desc',
    },
    debounceDelay: SEARCH_DEBOUNCE_DELAY,
    refetchOnMount: 'always',
  });

  const feeSummary = summary as FeeRegisterSummary | null;
  const customDateRange = dateRangePreset === 'custom';

  // Presets resolve client-side to concrete dates, same as the Invoices page.
  const { fromDate, toDate } = useMemo(() => {
    if (customDateRange) {
      return { fromDate: customFromDate, toDate: customToDate };
    }
    const range = getInvoiceDateRange(dateRangePreset);
    return { fromDate: range.from, toDate: range.to };
  }, [customDateRange, customFromDate, customToDate, dateRangePreset]);

  useEffect(() => {
    setFilters({
      search_term: searchInput || null,
      types_filter: typeFilter.length > 0 ? typeFilter : null,
      client_filter: clientFilter || null,
      status_filter: null,
      workflow_status_ids: null,
      bucket_filter: bucketFilter === ALL_BUCKETS ? null : bucketFilter,
      from_date: fromDate || null,
      to_date: toDate || null,
      page_size: pageSize,
      page_offset: (page - 1) * pageSize,
    });
  }, [
    searchInput,
    typeFilter,
    clientFilter,
    bucketFilter,
    fromDate,
    toDate,
    page,
    pageSize,
    setFilters,
  ]);

  // Back from the invoice builder: reopen the modal it was opened from, on
  // the same tab. Uses the restore snapshot, not a register row — the row may
  // be on a page that isn't loaded any more.
  const { restoredProps, clearRestored } = useRestoredJobCardModal();

  const handleRowClick = useCallback((row: FeeRegisterRow) => {
    clearRestored();
    setSelectedProjectId(row.project_id);
    setSelectedStageId(row.stage_id);
    setSelectedRow(row);
    setIsModalOpen(true);
  }, [clearRestored]);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedProjectId(null);
    setSelectedStageId(null);
    setSelectedRow(null);
    clearRestored();
    refetch();
  }, [refetch, clearRestored]);

  const handleDateRangeChange = useCallback((v: string | string[]) => {
    const next = Array.isArray(v) ? v[0] ?? '' : v;
    setDateRangePreset(next);
    if (next === 'custom') {
      // Seed the custom inputs with the current month so the range is valid
      // the moment it's shown.
      const seed = getInvoiceDateRange('thisMonth');
      setCustomFromDate(seed.from);
      setCustomToDate(seed.to);
    } else {
      setCustomFromDate('');
      setCustomToDate('');
    }
    setPage(1);
  }, []);

  const handleOutcome = useCallback(
    async (row: FeeRegisterRow, outcome: FeeOutcome) => {
      const key = `${row.project_id}-${row.stage_id ?? 'p'}`;
      setActioningKey(key);
      setActionError(null);
      try {
        const { data, error: rpcError } = await supabase.rpc('set_fee_outcome', {
          p_project_id: row.project_id,
          p_stage_id: row.stage_id,
          p_outcome: outcome,
        });
        if (rpcError) throw rpcError;
        if (data && !data.success) {
          throw new Error(data.error || 'Failed to update outcome');
        }
        setConfirmLostKey(null);
        setChangeRow(null);
        // Refetch rather than patch locally: the row changes bucket, so the
        // pill counts and (under a bucket filter) its membership both move.
        await refetch();
      } catch (err) {
        console.error('Error setting fee outcome:', err);
        setActionError(err instanceof Error ? err.message : 'Failed to update outcome');
      } finally {
        setActioningKey(null);
      }
    },
    [supabase, refetch],
  );

  const typeItems: DropdownItem[] = useMemo(
    () => PROJECT_TYPE_OPTIONS.map(o => ({ id: o.id, label: o.label, value: o.value })),
    []
  );

  const dateRangeItems: DropdownItem[] = useMemo(
    () => DATE_RANGE_PRESETS.map(p => ({ id: p.value, label: p.label, value: p.value })),
    []
  );

  const isNarrow = useNarrowViewport();

  const hasActiveFilters =
    searchInput.length > 0 ||
    bucketFilter !== ALL_BUCKETS ||
    clientFilter !== '' ||
    dateRangePreset !== DEFAULT_DATE_RANGE_PRESET ||
    typeFilter.length !== DEFAULT_TYPE_FILTER.length ||
    typeFilter.some((t) => !DEFAULT_TYPE_FILTER.includes(t));

  const handleClearFilters = useCallback(() => {
    setSearchInput('');
    setTypeFilter(DEFAULT_TYPE_FILTER);
    setBucketFilter(ALL_BUCKETS);
    setClientFilter('');
    setDateRangePreset(DEFAULT_DATE_RANGE_PRESET);
    setCustomFromDate('');
    setCustomToDate('');
    setPage(1);
  }, []);

  const statusOptions = useMemo(
    () =>
      FEE_BUCKET_PILLS.map(pill => ({
        value: pill.value,
        label: pill.label,
        count: feeSummary?.[pill.countKey] ?? 0,
      })),
    [feeSummary],
  );

  const columns = useMemo<ColumnDef<FeeRegisterRow>[]>(() => [
    {
      accessorKey: 'job_number',
      header: 'Job #',
      meta: { minWidth: '96px', width: '96px' },
      cell: ({ row }) => {
        const r = row.original;
        if (r.job_number == null) return <span>—</span>;
        return (
          <span>
            {r.job_number}
            {r.stage_id != null && r.stage_code ? `.${r.stage_code}` : ''}
          </span>
        );
      },
    },
    {
      accessorKey: 'description',
      header: 'Description',
      meta: { minWidth: '220px', width: 'auto' },
      cell: ({ row }) => {
        const r = row.original;
        const full = r.stage_id != null
          ? `${r.description} · Stage ${r.stage_code}${r.stage_description ? ` — ${r.stage_description}` : ''}`
          : r.description;
        return (
          <span title={full}>
            {r.description}
            {r.stage_id != null && (
              <span className="fees-page__stage-note">
                · Stage {r.stage_code}{r.stage_description ? ` — ${r.stage_description}` : ''}
              </span>
            )}
          </span>
        );
      },
    },
    {
      accessorKey: 'client_name',
      header: 'Client',
      meta: { minWidth: '160px', width: '18%' },
      cell: ({ row }) => (
        <span title={row.original.client_name ?? undefined}>
          {row.original.client_name ?? '—'}
        </span>
      ),
    },
    {
      accessorKey: 'fee_bucket',
      header: 'Fee Status',
      meta: { minWidth: '124px', width: '124px' },
      cell: ({ row }) => {
        const r = row.original;
        // The underlying workflow stage isn't a column any more, so surface it
        // on hover -- otherwise nothing on the page explains why a row reads
        // Won or Lost. Older rows fall back to the legacy job_status enum.
        const stage = r.workflow_status_name ?? r.project_status;
        const stageNote = stage
          ? `Workflow stage: ${stage}${r.workflow_status_name ? '' : ' (legacy status)'}`
          : 'No workflow stage set';
        return (
          <span title={stageNote}>
            <StatusPill label={r.fee_bucket} tone={feeBucketTone[r.fee_bucket] ?? 'neutral'} />
          </span>
        );
      },
    },
    {
      accessorKey: 'types',
      header: 'Type',
      meta: { minWidth: '104px', width: '104px' },
      cell: ({ row }) => {
        const t = row.original.types;
        if (!t) return <span>—</span>;
        return (
          <span className={`ui-chip type-chip type-chip--${t.toLowerCase().replace(/\s+/g, '-')}`}>
            {t}
          </span>
        );
      },
    },
    {
      accessorKey: 'fee_total',
      header: 'Fee Total',
      meta: { minWidth: '124px', width: '124px', align: 'right' },
      cell: ({ row }) => {
        const hasFee = row.original.fee_id != null;
        if (!hasFee) return <span className="fees-page__no-fee">—</span>;
        return <span>{formatCurrency(row.original.fee_total)}</span>;
      },
    },
    {
      accessorKey: 'fee_updated_at',
      header: 'Date',
      meta: { minWidth: '120px', width: '120px' },
      // Reads differently per stage of the pipeline, because "how long has this
      // been sitting" and "when was it decided" are different questions:
      //   Awaiting Fee -> days since the job was created (we owe them a fee)
      //   Pending      -> days since the fee's items last changed
      //   Won / Lost   -> the date the outcome was recorded
      cell: ({ row }) => {
        const r = row.original;

        if (r.fee_bucket === 'Won' || r.fee_bucket === 'Lost') {
          const decided = formatDate(r.outcome_at);
          if (!decided) {
            // No decision was ever recorded for this row: its outcome is
            // inferred from the workflow status (or legacy invoice history)
            // rather than set from here, so there is no date to show. Populates
            // as soon as Won/Lost or Change is used.
            return (
              <span
                className="fees-page__no-fee"
                title={`Outcome inferred from the workflow status — no ${r.fee_bucket.toLowerCase()} date was recorded`}
              >
                —
              </span>
            );
          }
          return <span title={`Marked ${r.fee_bucket.toLowerCase()} on this date`}>{decided}</span>;
        }

        const isPending = r.fee_bucket === 'Pending';
        const since = isPending
          ? (r.fee_items_updated_at ?? r.fee_updated_at)
          : r.entity_created_at;
        const age = formatDaysSince(since);
        if (!age) return <span>—</span>;
        return (
          <span
            title={
              isPending
                ? `Fee last worked on ${formatDate(since)}`
                : `Job created ${formatDate(since)} — no fee yet`
            }
          >
            {age}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: 'Action',
      meta: { minWidth: '176px', width: '176px' }, // 24px gutter + Won/Lost pair
      cell: ({ row }) => {
        const r = row.original;
        const key = `${r.project_id}-${r.stage_id ?? 'p'}`;
        const busy = actioningKey === key;

        // A decided row shows a single neutral Change button rather than a
        // Won/Lost pair. Two coloured buttons on a settled row read as a live
        // choice and invite a stray click that silently flips the job.
        if (DECIDED_BUCKETS.includes(r.fee_bucket)) {
          return (
            <div className="fees-page__outcome-actions" onClick={(e) => e.stopPropagation()}>
              {/* Reuses .tax-invoice-pill from sass/invoice-register.scss -- the
                  app's row-action button, defined globally rather than scoped to
                  the invoices page. Sharing the class instead of copying its
                  values keeps the two registers from drifting apart. The --edit
                  variant and pencil match the Invoices edit action, which is the
                  same idea: open a dialog to change an existing record. */}
              <button
                type="button"
                className="tax-invoice-pill tax-invoice-pill--edit"
                disabled={busy}
                title={`Change outcome (currently ${r.fee_bucket})`}
                onClick={() => { setActionError(null); setChangeRow(r); }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                Change
              </button>
            </div>
          );
        }

        const confirming = confirmLostKey === key;
        return (
          <div className="fees-page__outcome-actions" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="fee-outcome-btn fee-outcome-btn--won"
              disabled={busy}
              title="Mark won — moves to the first stage of the programme"
              onClick={() => handleOutcome(r, 'won')}
            >
              Won
            </button>
            <button
              type="button"
              className={`fee-outcome-btn fee-outcome-btn--lost${confirming ? ' is-confirming' : ''}`}
              disabled={busy}
              title="Mark unsuccessful"
              onClick={() => {
                if (confirming) {
                  handleOutcome(r, 'lost');
                } else {
                  setConfirmLostKey(key);
                }
              }}
            >
              {confirming ? 'Confirm?' : 'Lost'}
            </button>
          </div>
        );
      },
    },
  ], [actioningKey, confirmLostKey, handleOutcome]);

  const getRowId = useCallback(
    (row: FeeRegisterRow) => `${row.project_id}-${row.stage_id ?? 'p'}`,
    [],
  );

  if (!session) return null;

  const currentPage = pagination?.page ?? page;
  const currentPageSize = pagination?.page_size ?? pageSize;

  // Build a minimal JobCard-like object so JobCardModal can render header info even without a view row
  const headerJobNumber = selectedRow
    ? selectedRow.stage_id != null && selectedRow.stage_code
      ? `${selectedRow.job_number ?? ''}.${selectedRow.stage_code}`
      : String(selectedRow.job_number ?? '')
    : '';
  const placeholderJobCard: JobCard | null = selectedRow
    ? {
        project_id: selectedRow.project_id,
        parent_project_id: selectedRow.project_id,
        stage_row_id: selectedRow.stage_id,
        project_name: `${headerJobNumber ? headerJobNumber + ' · ' : ''}${selectedRow.description}${
          selectedRow.stage_id != null && selectedRow.stage_code
            ? ` [Stage ${selectedRow.stage_code}${selectedRow.stage_description ? ` — ${selectedRow.stage_description}` : ''}]`
            : ''
        }`,
        job_number: headerJobNumber,
        workflow_status: selectedRow.workflow_status_name,
        project_lead_name: '',
        client_name: selectedRow.client_name,
        allocations: [],
        first_allocation_date: '',
        last_allocation_date: '',
        total_hours: 0,
        total_cost: 0,
        invoices: null,
        total_invoiced: 0,
        invoice_count: 0,
      }
    : null;

  return (
    <ListPage
      title="Fees"
      className="fees-page"
      loading={loading && rows.length === 0 && !feeSummary}
      skeleton={{ segmentCount: 5, filterCount: 3, showAction: false }}
      search={
        <SearchInput
          placeholder="Search project, job number, client…"
          value={searchInput}
          onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
          onClear={() => { setSearchInput(''); setPage(1); }}
          aria-label="Search fees"
        />
      }
      segments={
        <StatusSegments
          aria-label="Fee status"
          size={isNarrow ? 'sm' : 'md'}
          value={bucketFilter}
          onChange={(v) => { setBucketFilter(v); setPage(1); }}
          segments={statusOptions.map((o) => ({ value: o.value, label: o.label, count: o.count }))}
        />
      }
      filters={
        <>
          <FilterTrigger
            label="Type"
            active={typeFilter.length !== DEFAULT_TYPE_FILTER.length || typeFilter.some((t) => !DEFAULT_TYPE_FILTER.includes(t))}
          >
            <Dropdown
              items={typeItems}
              value={typeFilter}
              onChange={(v) => { setTypeFilter(Array.isArray(v) ? v : [v]); setPage(1); }}
              placeholder="All"
              multiSelect
              searchable
            />
          </FilterTrigger>
          <FilterTrigger label="Client" active={clientFilter !== ''}>
            <SearchableClientDropdown
              value={clientFilter || null}
              onChange={(clientId) => {
                setClientFilter(clientId == null || clientId === 'none' ? '' : String(clientId));
                setPage(1);
              }}
              placeholder="All"
              allowNone
              noneLabel="All Clients"
            />
          </FilterTrigger>
          <FilterTrigger label="Date" active={dateRangePreset !== DEFAULT_DATE_RANGE_PRESET}>
            <Dropdown
              items={dateRangeItems}
              value={dateRangePreset}
              onChange={handleDateRangeChange}
              placeholder="All time"
              searchable={false}
            />
          </FilterTrigger>
          {customDateRange && (
            <>
              <FilterTrigger label="From" active>
                <TextInput size="sm" type="date" value={customFromDate} onChange={(e) => { setCustomFromDate(e.target.value); setPage(1); }} aria-label="From date" />
              </FilterTrigger>
              <FilterTrigger label="To" active>
                <TextInput size="sm" type="date" value={customToDate} onChange={(e) => { setCustomToDate(e.target.value); setPage(1); }} aria-label="To date" />
              </FilterTrigger>
            </>
          )}
        </>
      }
      hasActiveFilters={hasActiveFilters}
      onClearFilters={handleClearFilters}
      resultsText={
        !loading && pagination && feeSummary && (
          <>
            <span>
              Showing {pagination.total_records === 0 ? 0 : ((currentPage - 1) * currentPageSize) + 1}–{Math.min(currentPage * currentPageSize, pagination.total_records)} of{' '}
              {pagination.total_records.toLocaleString()} {pagination.total_records === 1 ? 'job' : 'jobs'}
            </span>
            <span className="list-page__stat">
              {bucketFilter === ALL_BUCKETS ? 'Total fee' : `Total fee (${bucketFilter})`}
              {/* Awaiting Fee rows have no fee set yet, so a $0.00 would read
                  as a real figure rather than an absence. */}
              {bucketFilter === 'Awaiting Fee' ? (
                <b title="These jobs don't have a fee yet">—</b>
              ) : (
                <b>{formatCurrency(feeSummary.filtered_fee_total)}</b>
              )}
            </span>
          </>
        )
      }
      footer={
        <Pagination
          variant="bar"
          currentPage={currentPage}
          totalPages={pagination?.total_pages ?? 1}
          totalRecords={pagination?.total_records ?? 0}
          pageSize={currentPageSize}
          onPageChange={setPage}
          onPageSizeChange={(sz) => { setPageSize(sz); setPage(1); }}
          hasNext={pagination?.has_next ?? false}
          hasPrevious={pagination?.has_previous ?? false}
          loading={loading || filtering}
          showPageInfo
          pageSizeOptions={[25, 50, 100]}
        />
      }
    >
      {actionError && <div className="list-page__error">{actionError}</div>}
      {error && (
        <div className="list-page__error">
          Error loading fees: {typeof error === 'string' ? error : 'Unknown error'}
        </div>
      )}

      {loading ? (
        <TableSkeleton compact rows={14} columns={8} showHeader />
      ) : rows.length === 0 ? (
        <div className="list-page__empty">No projects match these filters.</div>
      ) : (
        <div className={`list-page__body ${filtering ? 'is-filtering' : ''}`.trim()}>
          <Table
            data={rows}
            columns={columns}
            onRowClick={(row) => handleRowClick(row)}
            getRowId={getRowId}
            stickyHeader
          />
        </div>
      )}

      <ChangeOutcomeModal
        isOpen={changeRow !== null}
        row={changeRow}
        saving={actioningKey !== null}
        error={actionError}
        onClose={() => { setChangeRow(null); setActionError(null); }}
        onConfirm={handleOutcome}
      />

      <JobCardModal
        onClose={handleModalClose}
        {...(restoredProps ?? {
          isOpen: isModalOpen,
          jobCard: placeholderJobCard,
          projectId: selectedProjectId ?? undefined,
          stageId: selectedStageId,
          initialTab: 'fees' as const,
        })}
      />
    </ListPage>
  );
};

export default Fees;
